// <copyright file="quote-lock-badge.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { html, TemplateResult } from 'lit';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang';
import { QuoteLockState } from '../../../api/dealer-api-interface-all.js';

/**
 * The passive row indicator for a quote's LOCK state (Task 229184).
 *
 * <b>This is not the quote's state badge, and the distinction is the whole point.</b> A quote carries
 * two independent "something is happening" signals that look alike and come from different places:
 *
 *  - `QuoteState.Copying` is a FLAG ON THE QUOTE ITSELF, set on the DESTINATION of a copy while its
 *    rows are being written. `getQuoteStatus()` already renders it as the blue Copying badge.
 *  - `ViewQuoteSummary.lockState` is projected from the `locking.ResourceLock` row and describes the
 *    SOURCE — the quote being copied from, deleted, or held open in an editor. Nothing rendered it.
 *
 * The result was that copying quote A produced a visible Copying badge on the new copy B and NOTHING
 * on A, even though A was the one that had gone read-only. The lock was real and enforced (the server
 * refuses a second copy, a delete, or any user edit, and the row-action dialog greys those cards with
 * a reason) but it was only discoverable by opening the dialog. This helper is the passive half.
 *
 * <b>`Locked` ("in use") deliberately gets NO badge.</b> Only the states that can be kept honest are
 * badged. A copy announces both of its edges — `TaskDuplicateQuote` pushes the source when it starts,
 * `TaskReleaseCopyLock` pushes it when it finishes — so a Copying badge is live and self-clearing. The
 * interactive edit lock announces nothing: `TaskLockResource`/`TaskUnLockResource` push no event, and
 * its 5-minute TTL expires with no process running to say so, so a crashed browser or a slept laptop
 * cannot be detected at all. A badge that says "in use" after the lock is gone is worse than no badge:
 * it reports a restriction the server would not actually apply. If the lock tasks ever push on
 * release, revisit this — the missing piece is the event, not the rendering.
 *
 * Shared by the quote list and the project resource view for the same reason the capability model is:
 * the two surfaces must never disagree about what a row is showing.
 */
export function getQuoteLockBadge(lockState: QuoteLockState | undefined): TemplateResult | '' {
  const kind = quoteLockBadgeKind(lockState);
  if (!kind) return '';
  return html`<span class="badge badge-lock badge-lock-${kind}">${textOf(kind)}</span>`;
}

/**
 * Which badge a lock state earns, as a CODE rather than a message — the same split `quote-row-actions`
 * uses for its block reasons, and for the same reason: this decision is the part worth testing, and
 * keeping it free of localisation means it can be tested on behaviour alone without standing up a
 * language registry. `null` means render nothing.
 *
 * Copying/Deleting mean the row is READ-ONLY; Locked only means someone has it open and merely blocks
 * copying. They stay distinct codes so a user can tell "this is busy, wait" from "someone else has
 * this" without opening the dialog.
 */
export function quoteLockBadgeKind(lockState: QuoteLockState | undefined): QuoteLockBadgeKind | null {
  switch (lockState) {
    case QuoteLockState.Copying:
      return 'copying';
    case QuoteLockState.Deleting:
      return 'deleting';
    default:
      // Everything else, INCLUDING `Locked`. See the note above on why "in use" has no badge.
      return null;
  }
}

export type QuoteLockBadgeKind = 'copying' | 'deleting';

function textOf(kind: QuoteLockBadgeKind): string {
  switch (kind) {
    case 'copying':
      return LangFranchiseeQuoteListView.lockBadgeCopying();
    case 'deleting':
      return LangFranchiseeQuoteListView.lockBadgeDeleting();
  }
}
